/**
 * Configuration options for retry-with-backoff logic
 */
export interface RetryOptions {
  /**
   * Maximum number of retry attempts (not including the initial attempt)
   * @default 2
   */
  maxAttempts?: number;

  /**
   * Base delay in milliseconds for exponential backoff
   * Actual delay = baseDelay * (2 ^ attemptNumber)
   * @default 1000
   */
  baseDelay?: number;

  /**
   * Optional function to determine if an error should trigger a retry
   * Return true to retry, false to throw immediately
   * @default () => true (retry all errors)
   */
  shouldRetry?: (error: unknown, attempt: number) => boolean;

  /**
   * Optional function to extract retry delay from error response
   * Useful for respecting rate-limit headers like "Retry-After"
   * Return delay in milliseconds, or undefined to use exponential backoff
   * @default undefined
   */
  getRetryDelay?: (error: unknown, attempt: number) => number | undefined;

  /**
   * Optional callback invoked before each retry attempt
   * Useful for logging or metrics
   */
  onRetry?: (error: unknown, attempt: number, delay: number) => void;
}

/**
 * Error thrown when all retry attempts have been exhausted
 */
export class RetryExhaustedError extends Error {
  constructor(
    message: string,
    public readonly lastError: unknown,
    public readonly attempts: number,
  ) {
    super(message);
    this.name = "RetryExhaustedError";
  }
}
